"use client";

import type Lenis from "lenis";
import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

// Define global variables to store the Lenis instance and viewport state
declare global {
	interface Window {
		lenisInstance?: Lenis; // optional property
		isDesktopView?: boolean; // track viewport state globally
	}
}

type Orientation = "horizontal" | "vertical";

const LenisContext = createContext<React.RefObject<Lenis | null> | null>(null);
export const useLenis = () => useContext(LenisContext);

/**
 * ScrollProvider
 *
 * Owns the main Lenis instance imperatively. The React tree below this
 * provider never changes shape: children are rendered exactly once and
 * Lenis is attached to `window` from an effect. (The previous version
 * swapped children into a lazily loaded `<ReactLenis>` wrapper after
 * load, which unmounted and remounted the entire page a second time.)
 */
export function ScrollProvider({ children }: { children: React.ReactNode }) {
	const lenisRef = useRef<Lenis | null>(null);
	const [isDesktop, setIsDesktop] = useState(false);
	const [lenisReady, setLenisReady] = useState(false);
	const [forcedOrientation, setForcedOrientation] =
		useState<Orientation | null>(null);
	const [lenisDisabled, setLenisDisabled] = useState(false);

	// Track previous viewport state to detect changes
	const prevIsDesktopRef = useRef<boolean | null>(null);

	// Detect viewport size and set isDesktop state
	useEffect(() => {
		function handleResize() {
			const isDesktopView = window.matchMedia("(min-width: 768px)").matches;
			setIsDesktop(isDesktopView);

			// Store viewport state globally for access in other components
			window.isDesktopView = isDesktopView;

			// Reset scroll position when switching between modes
			if (
				prevIsDesktopRef.current !== null &&
				prevIsDesktopRef.current !== isDesktopView
			) {
				// Use a slight delay to ensure DOM has updated
				setTimeout(() => {
					if (window.lenisInstance) {
						window.lenisInstance.scrollTo(0, { immediate: true });
					}
					window.scrollTo(0, 0);
					const scrollContainer = document.getElementById("horizontal-scroll");
					if (scrollContainer) {
						scrollContainer.scrollLeft = 0;
					}
				}, 50);
			}

			prevIsDesktopRef.current = isDesktopView;
		}

		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Pages can force an orientation or disable Lenis via <html data-*> attributes
	useEffect(() => {
		const root = document.documentElement;

		const readAttrs = () => {
			const attr = root.dataset.lenisOrientation;
			if (attr === "horizontal" || attr === "vertical") {
				setForcedOrientation(attr);
			} else {
				setForcedOrientation(null);
			}
			setLenisDisabled(root.dataset.lenisDisabled === "true");
		};

		readAttrs();

		const observer = new MutationObserver(readAttrs);
		observer.observe(root, {
			attributes: true,
			attributeFilter: ["data-lenis-orientation", "data-lenis-disabled"],
		});

		return () => observer.disconnect();
	}, []);

	// Start Lenis once the browser is idle so it never competes with hydration
	useEffect(() => {
		if (lenisReady) return;

		let cancelled = false;
		let timeoutId: number | null = null;
		let idleHandle: number | null = null;

		const start = () => {
			if (!cancelled) setLenisReady(true);
		};

		if (typeof window.requestIdleCallback === "function") {
			idleHandle = window.requestIdleCallback(start, { timeout: 500 });
		} else {
			timeoutId = window.setTimeout(start, 160);
		}

		return () => {
			cancelled = true;
			if (
				idleHandle !== null &&
				typeof window.cancelIdleCallback === "function"
			) {
				window.cancelIdleCallback(idleHandle);
			}
			if (timeoutId !== null) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [lenisReady]);

	const orientation: Orientation =
		forcedOrientation ?? (isDesktop ? "horizontal" : "vertical");
	const isHorizontal = orientation === "horizontal";

	// Create (and re-create on orientation change) the Lenis instance
	useEffect(() => {
		if (!lenisReady || lenisDisabled) return;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

		let cancelled = false;
		let instance: Lenis | null = null;

		import("lenis")
			.then(({ default: LenisCtor }) => {
				if (cancelled) return;
				instance = new LenisCtor({
					orientation,
					gestureOrientation: isHorizontal ? "both" : "vertical",
					smoothWheel: true,
					wheelMultiplier: 0.8,
					touchMultiplier: isHorizontal && isDesktop ? 2 : 1,
					syncTouch: isHorizontal && isDesktop,
					syncTouchLerp: 0.1,
					duration: isHorizontal ? 1.2 : 0.8,
					...(isHorizontal ? {} : { lerp: 0.18 }),
					easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
					autoRaf: true,
				});
				lenisRef.current = instance;
				window.lenisInstance = instance;
			})
			.catch(() => {
				// Gracefully degrade to native scrolling when Lenis fails to load
			});

		return () => {
			cancelled = true;
			if (instance) {
				instance.destroy();
				if (lenisRef.current === instance) lenisRef.current = null;
				if (window.lenisInstance === instance) delete window.lenisInstance;
			}
		};
	}, [lenisReady, lenisDisabled, orientation, isHorizontal, isDesktop]);

	return (
		<LenisContext.Provider value={lenisRef}>{children}</LenisContext.Provider>
	);
}
